import axios from 'axios';

// Configuración según el entorno
// En desarrollo usa localhost:3001, en staging/prod usa URLs públicas
const API_URL = process.env.REACT_APP_API_URL || '/api';
const ENV = process.env.REACT_APP_ENV || 'production';
const DEBUG = process.env.REACT_APP_DEBUG === 'true';
const LOG_LEVEL = process.env.REACT_APP_LOG_LEVEL || 'error';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);

// Log de configuración en desarrollo
if (DEBUG || ENV === 'development') {
  console.log('🔧 API Configuration:');
  console.log(`  Environment: ${ENV}`);
  console.log(`  API URL: ${API_URL}`);
  console.log(`  Debug Mode: ${DEBUG}`);
  console.log(`  Log Level: ${LOG_LEVEL}`);
  console.log(`  API Timeout: ${API_TIMEOUT}ms`);
}

// Configurar credenciales globalmente (envía cookies httpOnly automáticamente)
axios.defaults.withCredentials = true;
axios.defaults.timeout = API_TIMEOUT;

// Interceptor para añadir el token a todas las peticiones
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log de requests en modo debug
    if (DEBUG) {
      console.log(`📡 Request: ${config.method.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
axios.interceptors.response.use(
  (response) => {
    if (DEBUG) {
      console.log(`✅ Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // Log detallado de errores
    if (LOG_LEVEL === 'debug' || LOG_LEVEL === 'info') {
      console.error('❌ API Error:', {
        status: error.response?.status,
        message: error.response?.data?.mensaje || error.message,
        url: error.config?.url,
      });
    }
    
    // Manejar errores de autenticación (no en /auth/me, que es la comprobación de sesión al arrancar,
    // ni en /auth/login, donde un 401 es "contraseña incorrecta" y la pantalla de login lo muestra)
    const url = error.config?.url || '';
    if (error.response?.status === 401 && !url.includes('/auth/me') && !url.includes('/auth/login')) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      // Llamar logout para limpiar cookie en el servidor
      axios.post(`${API_URL}/auth/logout`).catch(() => {});
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Autenticación
// getUploadUrl: token en la URL de /uploads/ (imágenes). getDownloadUrl: lo mismo, para enlaces de descarga
export { getUploadUrl, getDownloadUrl } from './descargas';

export const login = (email, password) => axios.post(`${API_URL}/auth/login`, { email, password });
export const register = (userData) => axios.post(`${API_URL}/auth/register`, userData);
export const logout = () => axios.post(`${API_URL}/auth/logout`);
export const getMe = () => axios.get(`${API_URL}/auth/me`);
export const forgotPassword = (email) => axios.post(`${API_URL}/auth/forgot-password`, { email });
export const resetPassword = (token, password) => axios.post(`${API_URL}/auth/reset-password`, { token, password });

// Usuarios
export const getUsuarios = (params) => axios.get(`${API_URL}/usuarios`, { params });   // { estado: 'baja' } -> dados de baja
export const reactivarUsuario = (id) => axios.patch(`${API_URL}/usuarios/${id}/reactivar`);
export const updateUsuario = (id, userData) => axios.put(`${API_URL}/usuarios/${id}`, userData);
export const cambiarRol = (id, rol) => axios.patch(`${API_URL}/usuarios/${id}/rol`, { rol });
export const cambiarPassword = (id, password) => axios.patch(`${API_URL}/usuarios/${id}/password`, { password });
export const eliminarUsuario = (id) => axios.delete(`${API_URL}/usuarios/${id}`);
export const hacerSuperadmin = (id) => axios.patch(`${API_URL}/usuarios/${id}/hacer-superadmin`);

// Incidencias
export const getIncidencias = (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  return axios.get(`${API_URL}/incidencias${params ? `?${params}` : ''}`);
};
export const getIncidenciasStats = () => axios.get(`${API_URL}/incidencias/stats`);
export const getIncidencia = (id) => axios.get(`${API_URL}/incidencias/${id}`);
export const createIncidencia = (formData) =>
  axios.post(`${API_URL}/incidencias`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const updateIncidencia = (id, formData) =>
  axios.put(`${API_URL}/incidencias/${id}`, formData);
export const deleteIncidencia = (id) => axios.delete(`${API_URL}/incidencias/${id}`);
export const getHistorialIncidencia = (id) => axios.get(`${API_URL}/incidencias/${id}/historial`);
export const valorarIncidencia = (id, rating, comentario) => axios.post(`${API_URL}/incidencias/${id}/valorar`, { rating, comentario });
export const asignarProveedor = (id, proveedor_id) => axios.patch(`${API_URL}/incidencias/${id}/asignar-proveedor`, { proveedor_id });
export const sugerirRespuestaIA = (id, contexto) => axios.post(`${API_URL}/incidencias/${id}/sugerir-respuesta`, contexto ? { contexto } : {});

// Comentarios
export const getComentarios = (incidenciaId) =>
  axios.get(`${API_URL}/incidencias/${incidenciaId}/comentarios`);
export const createComentario = (incidenciaId, comentario, visibilidad = 'comunidad', imagen = null) => {
  const fd = new FormData();
  fd.append('comentario', comentario);
  fd.append('visibilidad', visibilidad);
  if (imagen) fd.append('imagen', imagen);
  return axios.post(`${API_URL}/incidencias/${incidenciaId}/comentarios`, fd);
};
export const deleteComentario = (incidenciaId, comentarioId) =>
  axios.delete(`${API_URL}/incidencias/${incidenciaId}/comentarios/${comentarioId}`);

// Comentarios del tablón
export const getTablonComentarios = (anuncioId) =>
  axios.get(`${API_URL}/tablon/${anuncioId}/comentarios`);
export const createTablonComentario = (anuncioId, comentario) =>
  axios.post(`${API_URL}/tablon/${anuncioId}/comentarios`, { comentario });

// Notificaciones
export const getNotificaciones = () => axios.get(`${API_URL}/notificaciones`);
export const marcarNotificacionLeida = (id) => axios.patch(`${API_URL}/notificaciones/${id}/leer`);
export const marcarTodasLeidas = () => axios.patch(`${API_URL}/notificaciones/leer-todas`);

// Gestión de suscriptores push (admin)
export const getPushSubscriptores = (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  return axios.get(`${API_URL}/admin/push-subscriptores${params ? `?${params}` : ''}`);
};
export const togglePushUsuario = (userId, pushHabilitado) =>
  axios.patch(`${API_URL}/admin/push-subscriptores/${userId}`, { push_habilitado: pushHabilitado });
export const togglePushRol = (rol, pushHabilitado, comunidadId) =>
  axios.patch(`${API_URL}/admin/push-subscriptores/bulk-rol`, { rol, push_habilitado: pushHabilitado, comunidad_id: comunidadId });

// Tablón de anuncios
export const getAnuncios = (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  return axios.get(`${API_URL}/tablon${params ? `?${params}` : ''}`);
};
export const getAnuncio = (id) => axios.get(`${API_URL}/tablon/${id}`);
export const createAnuncio = (formData) =>
  axios.post(`${API_URL}/tablon`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const updateAnuncio = (id, formData) =>
  axios.put(`${API_URL}/tablon/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const deleteAnuncio = (id) => axios.delete(`${API_URL}/tablon/${id}`);

// Comunidades
export const getComunidades = () => axios.get(`${API_URL}/comunidades`);
export const getComunidad = (id) => axios.get(`${API_URL}/comunidades/${id}`);
export const crearComunidad = (comunidadData) => axios.post(`${API_URL}/comunidades`, comunidadData);
export const updateComunidad = (id, data) => axios.put(`${API_URL}/comunidades/${id}`, data);
export const deleteComunidad = (id) => axios.delete(`${API_URL}/comunidades/${id}`);

// Actividad
export const getActividad = () => axios.get(`${API_URL}/actividad`);

// Búsqueda global
export const buscarGlobal = (query) => axios.get(`${API_URL}/buscar?q=${encodeURIComponent(query)}`);

// Usuarios - crear manual
export const crearUsuarioManual = (userData) => axios.post(`${API_URL}/usuarios/crear`, userData);

// Configuración del sistema
export const getConfiguracionSistema = (comunidad_id = null) => axios.get(`${API_URL}/configuracion-sistema${comunidad_id ? `?comunidad_id=${comunidad_id}` : ''}`);
export const updateConfiguracionSistema = (data) => axios.put(`${API_URL}/configuracion-sistema`, data);

// Proveedores
export const getProveedores = (comunidad_id = null) => {
  const params = comunidad_id ? `?comunidad_id=${comunidad_id}` : '';
  return axios.get(`${API_URL}/proveedores${params}`);
};
export const getProveedor = (id) => axios.get(`${API_URL}/proveedores/${id}`);
export const createProveedor = (proveedorData) => axios.post(`${API_URL}/proveedores`, proveedorData);
export const updateProveedor = (id, proveedorData) => axios.put(`${API_URL}/proveedores/${id}`, proveedorData);
export const deleteProveedor = (id) => axios.delete(`${API_URL}/proveedores/${id}`);

// Documentos
export const getDocumentos = (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  return axios.get(`${API_URL}/documentos${params ? `?${params}` : ''}`);
};
export const createDocumento = (formData) =>
  axios.post(`${API_URL}/documentos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
export const deleteDocumento = (id) => axios.delete(`${API_URL}/documentos/${id}`);
export const updateDocumento = (id, data) => axios.put(`${API_URL}/documentos/${id}`, data);

// Propietarios externos
export const getPropietariosExternos = (comunidad_id) =>
  axios.get(`${API_URL}/propietarios-externos?comunidad_id=${comunidad_id}`);
export const anadirPropietariosEmails = (comunidad_id, emails) =>
  axios.post(`${API_URL}/propietarios-externos`, { comunidad_id, emails });
export const deletePropietarioExterno = (id) => axios.delete(`${API_URL}/propietarios-externos/${id}`);
export const enviarDocumentoPropietarios = (docId) =>
  axios.post(`${API_URL}/documentos/${docId}/enviar-propietarios`);
export const enviarAnuncioPropietarios = (anuncioId) =>
  axios.post(`${API_URL}/tablon/${anuncioId}/enviar-propietarios`);

// Permisos
export const getPermisos = () => axios.get(`${API_URL}/permisos`);
export const getRolPermisos = (rol) => axios.get(`${API_URL}/roles/${rol}/permisos`);
export const assignPermisoToRol = (rol, permiso_id) => axios.post(`${API_URL}/roles/${rol}/permisos`, { permiso_id });
export const removePermisoFromRol = (rol, asignacion_id) => axios.delete(`${API_URL}/roles/${rol}/permisos/${asignacion_id}`);

// Votaciones
export const crearVotacion = (votacionData) => axios.post(`${API_URL}/votaciones`, votacionData);
export const getVotacionByAnuncio = (anuncio_id) => axios.get(`${API_URL}/votaciones/${anuncio_id}`);
export const votar = (votacionData) => axios.post(`${API_URL}/votos`, votacionData);
export const cerrarVotacion = (votacion_id) => axios.put(`${API_URL}/votaciones/${votacion_id}/cerrar`);

// Zonas comunes y reservas
export const getZonasComunes = (comunidad_id = null) => {
  const params = comunidad_id ? `?comunidad_id=${comunidad_id}` : '';
  return axios.get(`${API_URL}/zonas-comunes${params}`);
};
export const createZonaComun = (zonaData) => axios.post(`${API_URL}/zonas-comunes`, zonaData);
export const updateZonaComun = (id, zonaData) => axios.put(`${API_URL}/zonas-comunes/${id}`, zonaData);
export const deleteZonaComun = (id) => axios.delete(`${API_URL}/zonas-comunes/${id}`);

export const getReservas = (filters = {}) => {
  const params = new URLSearchParams(filters).toString();
  return axios.get(`${API_URL}/reservas${params ? `?${params}` : ''}`);
};
export const createReserva = (reservaData) => axios.post(`${API_URL}/reservas`, reservaData);
export const cancelReserva = (id) => axios.delete(`${API_URL}/reservas/${id}`);

// Tickets
export const getTickets = () => axios.get(`${API_URL}/tickets`);
export const createTicket = (data) => axios.post(`${API_URL}/tickets`, data);
export const updateTicket = (id, data) => axios.put(`${API_URL}/tickets/${id}`, data);

// Invitaciones
export const validarInvitacion = (token) => axios.get(`${API_URL}/invitaciones/validar/${token}`);
export const getInvitaciones = (comunidad_id = null) => {
  const params = comunidad_id ? `?comunidad_id=${comunidad_id}` : '';
  return axios.get(`${API_URL}/invitaciones${params}`);
};
export const crearInvitacion = (data) => axios.post(`${API_URL}/invitaciones`, data);
export const eliminarInvitacion = (id) => axios.delete(`${API_URL}/invitaciones/${id}`);

// Push Tokens
export const guardarPushToken = (token, plataforma = 'android') => axios.post(`${API_URL}/push-token`, { token, plataforma });
export const eliminarPushToken = (token) => axios.delete(`${API_URL}/push-token`, { data: { token } });

// Crear usuario manual (admin)
export const crearUsuario = (data) => axios.post(`${API_URL}/usuarios/crear`, data);

// Gestión de admins (solo superadmin)
export const getAdmins = () => axios.get(`${API_URL}/admins`);
export const asignarComunidadesAdmin = (adminId, comunidades_ids) =>
  axios.post(`${API_URL}/admins/${adminId}/comunidades`, { comunidades_ids });
export const desasignarComunidadAdmin = (adminId, comId) =>
  axios.delete(`${API_URL}/admins/${adminId}/comunidades/${comId}`);

// Gestión de grupos de admins (solo superadmin)
export const getGrupos = () => axios.get(`${API_URL}/grupos`);
export const crearGrupo = (data) => axios.post(`${API_URL}/grupos`, data);
export const actualizarGrupo = (id, data) => axios.put(`${API_URL}/grupos/${id}`, data);
export const eliminarGrupo = (id) => axios.delete(`${API_URL}/grupos/${id}`);
export const addAdminToGrupo = (grupoId, adminId) =>
  axios.post(`${API_URL}/grupos/${grupoId}/admins`, { admin_id: adminId });
export const removeAdminFromGrupo = (grupoId, adminId) =>
  axios.delete(`${API_URL}/grupos/${grupoId}/admins/${adminId}`);
export const addComunidadToGrupo = (grupoId, comunidadId) =>
  axios.post(`${API_URL}/grupos/${grupoId}/comunidades`, { comunidad_id: comunidadId });
export const removeComunidadFromGrupo = (grupoId, comId) =>
  axios.delete(`${API_URL}/grupos/${grupoId}/comunidades/${comId}`);

// ✅ ÚNICO export default (con todo dentro)
export default {
  login,
  register,
  getMe,
  forgotPassword,
  resetPassword,
  getUsuarios,
  updateUsuario,
  cambiarRol,
  cambiarPassword,
  getIncidencias,
  getIncidenciasStats,
  getIncidencia,
  createIncidencia,
  updateIncidencia,
  deleteIncidencia,
  getHistorialIncidencia,
  valorarIncidencia,
  asignarProveedor,
  sugerirRespuestaIA,
  getComentarios,
  createComentario,
  getNotificaciones,
  marcarNotificacionLeida,
  marcarTodasLeidas,
  getAnuncios,
  getAnuncio,
  createAnuncio,
  updateAnuncio,
  deleteAnuncio,
  getComunidades,
  getComunidad,
  crearComunidad,
  deleteComunidad,
  getActividad,
  buscarGlobal,
  crearUsuarioManual,
  getProveedores,
  getProveedor,
  createProveedor,
  updateProveedor,
  deleteProveedor,
  getDocumentos,
  createDocumento,
  deleteDocumento,
  getPermisos,
  getRolPermisos,
  assignPermisoToRol,
  removePermisoFromRol,
  getTablonComentarios,
  createTablonComentario,
  crearVotacion,
  getVotacionByAnuncio,
  votar,
  cerrarVotacion,
  getZonasComunes,
  createZonaComun,
  updateZonaComun,
  deleteZonaComun,
  getReservas,
  createReserva,
  cancelReserva,
  guardarPushToken,
  eliminarPushToken,
  getUserPreferences,
  updateUserPreferences,
  updatePassword,
  updateDocumento,
  getPropietariosExternos,
  anadirPropietariosEmails,
  enviarAnuncioPropietarios,
  deletePropietarioExterno,
  enviarDocumentoPropietarios,
};

// Preferencias de usuario
export const getUserPreferences = () => axios.get(`${API_URL}/user/preferences`);
export const updateUserPreferences = (preferences) => axios.put(`${API_URL}/user/preferences`, preferences);
export const updatePassword = (data) => axios.post(`${API_URL}/user/change-password`, data);

// Fichajes
// Ausencias
export const getAusencias = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return axios.get(`${API_URL}/ausencias${query ? `?${query}` : ''}`);
};
export const crearAusencia = (data) => axios.post(`${API_URL}/ausencias`, data);
export const actualizarAusencia = (id, data) => axios.put(`${API_URL}/ausencias/${id}`, data);
export const eliminarAusencia = (id) => axios.delete(`${API_URL}/ausencias/${id}`);

// Eventos
export const getEventos = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return axios.get(`${API_URL}/eventos${query ? '?' + query : ''}`);
};
export const crearEvento = (data) => axios.post(`${API_URL}/eventos`, data);
export const actualizarEvento = (id, data) => axios.put(`${API_URL}/eventos/${id}`, data);
export const eliminarEvento = (id) => axios.delete(`${API_URL}/eventos/${id}`);

// Vacaciones
export const getVacacionesResumen = (anio) => axios.get(`${API_URL}/vacaciones/resumen?anio=${anio}`);
export const actualizarVacacionesConfig = (usuarioId, data) => axios.put(`${API_URL}/vacaciones/config/${usuarioId}`, data);

export const getFichajeEstado = () => axios.get(`${API_URL}/fichajes/estado`);
export const getFichajesSinSalida = () => axios.get(`${API_URL}/fichajes/sin-salida`);
export const getFichajesConfig = () => axios.get(`${API_URL}/fichajes/configuracion`);
export const updateFichajesConfig = (data) => axios.put(`${API_URL}/fichajes/configuracion`, data);
export const ficharEntrada = (notas = '', latitud = null, longitud = null) => axios.post(`${API_URL}/fichajes/entrada`, { notas, latitud, longitud });
export const ficharSalida = (notas = '', latitud = null, longitud = null) => axios.post(`${API_URL}/fichajes/salida`, { notas, latitud, longitud });
export const getFichajes = (params = {}) => axios.get(`${API_URL}/fichajes`, { params });
export const toggleFichajesPermiso = (id, valor) => axios.patch(`${API_URL}/usuarios/${id}/fichajes-permiso`, { puede_gestionar_fichajes: valor });

// Planes / suscripciones
export const getPlanUsuario = (userId) => axios.get(`${API_URL}/superadmin/usuarios/${userId}/plan`);
export const actualizarPlanUsuario = (userId, data) => axios.put(`${API_URL}/superadmin/usuarios/${userId}/plan`, data);
// Planes por grupo
export const getPlanGrupo = (grupoId) => axios.get(`${API_URL}/superadmin/grupos/${grupoId}/plan`);
export const actualizarPlanGrupo = (grupoId, data) => axios.put(`${API_URL}/superadmin/grupos/${grupoId}/plan`, data);

// Módulos por grupo (solo superadmin)
export const getModulosGrupo = (grupoId) => axios.get(`${API_URL}/superadmin/grupos/${grupoId}/modulos`);
export const setModuloGrupo = (grupoId, modulo, activo) => axios.put(`${API_URL}/superadmin/grupos/${grupoId}/modulos`, { modulo, activo });
